import type {
  ImageProps,
  ShapeProps,
  Slide,
  TextProps,
  TextPropsTextConfig,
} from '@features/powerpoint';
import { themeV2 } from '../domain/theme-v2';
import { DEFAULT_FONT } from '../utils/constants-v2';
import { contentSlideHeaderV2 } from '../utils/slide-header-v2';
import { dateRangeToString } from '@utils/dates';
import { campaignDateIcon, chartIcon } from '@features/powerpoint/images/icons';
import {
  convertSlugToDimensionName,
  validDimensionSlug,
} from '@features/reports';

type DatesWithIcon = {
  header: {
    text: string;
    options: TextProps['options'];
  };
  dates: {
    text: string;
    options: TextProps['options'];
  };
  image: {
    path: string;
  };
};

export const TOP_BOTTOM_NAMES = ['Top', 'Bottom'] as const;

export const isTopBottomName = (
  name: string
): name is (typeof TOP_BOTTOM_NAMES)[number] => {
  return (TOP_BOTTOM_NAMES as readonly string[]).includes(name);
};

const fieldHeaderOptions: TextProps['options'] = {
  h: 0.24,
  w: 3.26,
  color: themeV2.colors.inmarketGreen,
  fontSize: 10,
  bold: true,
  fontFace: DEFAULT_FONT,
} as const;

const fieldValueOptions: TextProps['options'] = {
  color: themeV2.colors.metricBlue,
  h: 0.34,
  w: 3.26,
  fontSize: 16,
  fontFace: DEFAULT_FONT,
} as const;

const mapToTextProps = (
  sortedNameCount: [string, number][]
): TextPropsTextConfig[] =>
  sortedNameCount.map(([key, count]) => ({
    text: `${validDimensionSlug(key) ? convertSlugToDimensionName(key) : key} (${count})`,
    options: {
      color: themeV2.colors.black,
      breakLine: true,
      bullet: true,
      fontSize: 10,
      fontFace: DEFAULT_FONT,
    },
  }));

const getDimensionReport = (dimensionData: string[]): TextPropsTextConfig[] => {
  const nameCount: Record<string, number> = dimensionData.reduce(
    (acc, name) => {
      acc[name] = (acc[name] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const filteredNameCount = Object.entries(nameCount).filter(
    ([key]) => !isTopBottomName(key)
  );

  const sortedFilteredNameCount = filteredNameCount.toSorted(
    ([_, countA], [__, countB]) => countB - countA
  );

  return mapToTextProps(sortedFilteredNameCount);
};

const METRIC_CARD_WIDTH = 3.08;
const METRIC_CARD_HEIGHT = 2.1;
const METRIC_CARD_START_X = 0.32;
const CARD_GAP = 0.12;
const METRIC_CARD_ROW_1_Y = 2.01;
const METRIC_CARD_ROW_2_Y = 4.26;
const DIMENSION_CARD_X =
  METRIC_CARD_START_X + 3 * (METRIC_CARD_WIDTH + CARD_GAP);

const CARD_STYLE: Pick<ShapeProps['options'], 'fill' | 'rectRadius' | 'line'> =
  {
    fill: { color: 'FFFFFF' },
    rectRadius: 0.1,
    line: { color: 'D0D5DD', width: 0.75 },
  };

const metricCardPositions = [
  { x: METRIC_CARD_START_X, y: METRIC_CARD_ROW_1_Y },
  {
    x: METRIC_CARD_START_X + (METRIC_CARD_WIDTH + CARD_GAP),
    y: METRIC_CARD_ROW_1_Y,
  },
  {
    x: METRIC_CARD_START_X + 2 * (METRIC_CARD_WIDTH + CARD_GAP),
    y: METRIC_CARD_ROW_1_Y,
  },
  { x: METRIC_CARD_START_X, y: METRIC_CARD_ROW_2_Y },
  {
    x: METRIC_CARD_START_X + (METRIC_CARD_WIDTH + CARD_GAP),
    y: METRIC_CARD_ROW_2_Y,
  },
] as const;

const buildDatesWithIcon = (
  fieldHeader: string,
  dates: [string, string],
  image: string
): DatesWithIcon => {
  return {
    header: { text: fieldHeader, options: { ...fieldHeaderOptions } },
    dates: {
      text: dateRangeToString(dates),
      options: { ...fieldValueOptions },
    },
    image: { path: image },
  };
};

const buildListWithHeader = (
  fieldHeader: TextProps,
  formattedMetrics: TextProps
): TextProps[] => {
  return [
    { text: fieldHeader.text, options: fieldHeader.options },
    { text: formattedMetrics.text, options: formattedMetrics.options },
  ];
};

type OverviewSlideV2Props = {
  flightDates: [string, string];
  measurementDates: [string, string];
  metrics: { name: string; value: string }[];
  reportsIncluded: string[];
  reportingDimensions: string[];
  slideMaster: string;
};

export const overviewSlideInterfaceV2 = (
  props: OverviewSlideV2Props
): Slide => {
  const formattedReportingDimensions = getDimensionReport(
    props.reportingDimensions
  );

  const datesWithIcons: DatesWithIcon[] = [
    buildDatesWithIcon(
      'Flight Dates'.toUpperCase(),
      props.flightDates,
      campaignDateIcon
    ),
    buildDatesWithIcon(
      'Measurement Period'.toUpperCase(),
      props.measurementDates,
      chartIcon
    ),
  ];

  const images: ImageProps[] = datesWithIcons.map((item, index) => ({
    path: item.image.path,
    h: 0.52,
    w: 0.52,
    x: index === 0 ? 0.39 : 4.18,
    y: 1.05,
  }));

  const dateTexts: TextProps[] = datesWithIcons.flatMap((item, index) => [
    {
      text: item.header.text.toUpperCase() || '',
      options: {
        ...fieldHeaderOptions,
        x: index === 0 ? 0.89 : 4.68,
        y: 1.08,
      },
    },
    {
      text: item.dates.text || '',
      options: {
        ...fieldValueOptions,
        x: index === 0 ? 0.89 : 4.68,
        y: 1.28,
      },
    },
  ]);

  const metricCardShapes = props.metrics
    .slice(0, metricCardPositions.length)
    .flatMap((_metric, index) => {
      const pos = metricCardPositions[index] ?? metricCardPositions[0];
      return [
        {
          shapeName: 'roundRect' as const,
          options: {
            x: pos.x,
            y: pos.y,
            w: METRIC_CARD_WIDTH,
            h: METRIC_CARD_HEIGHT,
            ...CARD_STYLE,
          },
        },
      ];
    });

  const metricCardTexts: TextProps[] = props.metrics
    .slice(0, metricCardPositions.length)
    .flatMap((metric, index) => {
      const pos = metricCardPositions[index] ?? metricCardPositions[0];
      return [
        {
          text: metric.name.toUpperCase(),
          options: {
            ...fieldHeaderOptions,
            x: pos.x,
            y: pos.y + 0.18,
            w: METRIC_CARD_WIDTH,
            align: 'center' as const,
          },
        },
        {
          text: metric.value,
          options: {
            color: themeV2.colors.metricBlue,
            fontSize: 30,
            bold: true,
            fontFace: DEFAULT_FONT,
            align: 'center' as const,
            x: pos.x,
            y: pos.y + 0.76,
            w: METRIC_CARD_WIDTH,
            h: 0.87,
          },
        },
      ];
    });

  const reportsIncludedPosition =
    metricCardPositions.length > props.metrics.length
      ? (metricCardPositions[props.metrics.length] ?? metricCardPositions[0])
      : {
          x: METRIC_CARD_START_X + 2 * (METRIC_CARD_WIDTH + CARD_GAP),
          y: METRIC_CARD_ROW_2_Y,
        };

  const reportsIncluded: TextPropsTextConfig[] = props.reportsIncluded.map(
    metric => ({
      text: metric,
      options: {
        color: themeV2.colors.black,
        breakLine: true,
        fontSize: 10,
        fontFace: DEFAULT_FONT,
        align: 'center' as const,
      },
    })
  );

  const textLists: TextProps[] = [
    ...buildListWithHeader(
      {
        text: 'Reports Included'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
          x: reportsIncludedPosition.x,
          y: reportsIncludedPosition.y + 0.18,
          align: 'center' as const,
          w: METRIC_CARD_WIDTH,
        },
      },
      {
        text: reportsIncluded,
        options: {
          h: 1.2,
          w: METRIC_CARD_WIDTH - 0.24,
          x: reportsIncludedPosition.x + 0.12,
          y: reportsIncludedPosition.y + 0.76,
          valign: 'top',
          paraSpaceAfter: 8,
        },
      }
    ),
    ...buildListWithHeader(
      {
        text: 'Reporting Dimensions'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
          x: DIMENSION_CARD_X + 0.13,
          y: METRIC_CARD_ROW_1_Y + 0.17,
          w: METRIC_CARD_WIDTH - 0.26,
        },
      },
      {
        text: formattedReportingDimensions,
        options: {
          h:
            METRIC_CARD_ROW_2_Y -
            METRIC_CARD_ROW_1_Y +
            METRIC_CARD_HEIGHT -
            0.68,
          w: METRIC_CARD_WIDTH - 0.13,
          x: DIMENSION_CARD_X + 0.13,
          y: METRIC_CARD_ROW_1_Y + 0.5,
          valign: 'top',
          paraSpaceAfter: 6,
        },
      }
    ),
  ];

  const reportsIncludedCard = {
    shapeName: 'roundRect' as const,
    options: {
      x: reportsIncludedPosition.x,
      y: reportsIncludedPosition.y,
      w: METRIC_CARD_WIDTH,
      h: METRIC_CARD_HEIGHT,
      ...CARD_STYLE,
    },
  };

  const dimensionCard = {
    shapeName: 'roundRect' as const,
    options: {
      x: DIMENSION_CARD_X,
      y: METRIC_CARD_ROW_1_Y,
      w: METRIC_CARD_WIDTH,
      h: METRIC_CARD_ROW_2_Y - METRIC_CARD_ROW_1_Y + METRIC_CARD_HEIGHT,
      ...CARD_STYLE,
    },
  };

  return {
    slideMaster: props.slideMaster,
    images,
    shapes: [...metricCardShapes, reportsIncludedCard, dimensionCard],
    texts: [
      contentSlideHeaderV2('Campaign Overview'),
      ...dateTexts,
      ...metricCardTexts,
      ...textLists,
    ],
  };
};
